/**
 * Shared create/edit form layout used inside {@link SlidePanel}.
 */

import * as React from 'react';
import { Button } from '@/components/forms/Button';
import { Spinner } from '@/components/ui/spinner';
import { Save, Plus, Edit, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Props for {@link SlidePanelForm}. */
interface SlidePanelFormProps {
  formType: 'create' | 'edit';
  title: string;
  subtitle?: string;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel: string;
  submitIcon?: LucideIcon;
  isSubmitting: boolean;
  isValid: boolean;
  isLoading?: boolean;
  /** Shrink-wrap a short form instead of filling the panel/dialog height. */
  compact?: boolean;
  children: React.ReactNode;
  className?: string;
}

/** Slide panel wrapping a form with loading and submit chrome. */
export const SlidePanelForm: React.FC<SlidePanelFormProps> = ({
  formType,
  title,
  subtitle,
  onClose,
  onSubmit,
  submitLabel,
  submitIcon = Save,
  isSubmitting,
  isValid,
  isLoading = false,
  compact = false,
  children,
  className,
}) => {
  if (isLoading) {
    return (
      <div className={cn('slide-panel-form slide-panel-form--loading', className)}>
        <Spinner size="lg" />
      </div>
    );
  }

  const Icon = formType === 'create' ? Plus : Edit;

  return (
    <div className={cn('slide-panel-form', compact && 'slide-panel-form--compact', className)}>
      <div className="slide-panel-form-header">
        <div className="slide-panel-form-header-content">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center border-2"
              style={{
                background: 'color-mix(in srgb, var(--color-2-nuance-1) 10%, transparent)',
                borderColor: 'var(--color-2-nuance-1)',
              }}
            >
              <Icon className="w-6 h-6" style={{ color: 'var(--color-2-nuance-1)' }} />
            </div>
            <div>
              <h2 className="slide-panel-form-title" style={{ color: 'var(--color-1-nuance-1)' }}>
                {title}
              </h2>
              {subtitle && <p className="slide-panel-form-subtitle">{subtitle}</p>}
            </div>
          </div>
        </div>
        <Button preset="form-close" onClick={onClose} disabled={isSubmitting} />
      </div>

      <div className="slide-panel-form-content scrollbar-thin">
        <div className="space-y-6">{children}</div>
      </div>

      <div className="slide-panel-form-footer">
        <div className="flex gap-3 ml-auto">
          <Button preset="form-cancel" onClick={onClose} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button
            preset="form-save"
            icon={submitIcon}
            onClick={onSubmit}
            loading={isSubmitting}
            disabled={!isValid || isSubmitting}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};
